<script setup lang="ts">
import { computed, ref, onBeforeMount, reactive, toRefs, watch } from "vue";
import {
  FormLabel,
  FormInput,
  FormCheck,
  FormSelect,
  FormTextarea
} from "../../../base-components/Form";
import Button from "../../../base-components/Button";
import Preview from "../../../base-components/Preview";
import LoadingIcon from "../../../base-components/LoadingIcon";
import { useProductosStore } from "../../../services/productos";
import { useMensajesStore } from "../../../services/mensajes";
import { IMPRESION_POR_DEFECTO, Producto } from "@fixeduy/restaurantestypes";
import { useTasasIVAStore } from "../../../services/tasas";
import { userUserStore } from "../../../stores/user";
import { round } from "lodash";
import { helpers, maxLength, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import Table from "../../../base-components/Table";
import InputNumero from "../../../custom-components/InputNumero/InputNumero.vue";
import { useMonedasStore } from "../../../services/monedas";
import Alert from "../../../base-components/Alert/Alert.vue";
import Lucide from "../../../base-components/Lucide/Lucide.vue";
import { useProductosCategoriasStore } from "../../../services/productosCategorias";
import TomSelect from "../../../custom-components/TomSelect/TomSelect.vue";

const productosStore = useProductosStore();
const mensajesStore = useMensajesStore();
const cargando = ref(false);
const errorCreacion = ref("");
const fotoFile = ref<File | null>(null);
const tasasStore = useTasasIVAStore();
const userStore = userUserStore();
const monedasStore = useMonedasStore();
const categoriasStore = useProductosCategoriasStore();
const categoriasActivasOrdenadas = computed(() =>
  categoriasStore.categorias
    .filter(categoria => categoria.activo)
    .sort((a, b) =>
      a.nombre.localeCompare(b.nombre, "es", { sensitivity: "base" })
    )
);

/** Lista de sectores de impresión según opciones generales (misma regla que en pedidos). */
function listaSectoresImpresionDesdeOg(og: {
  sectores?: string[];
  sectoresImpresion?: string[];
}) {
  return og.sectoresImpresion?.length ? og.sectoresImpresion : og.sectores ?? [];
}

/** Convierte `impresionPorDefecto` (tokens + nombres) en valores que coinciden con los checkboxes. */
function expandImpresionPorDefectoASectoresFormulario(
  def: string[] | undefined,
  sectoresDisponibles: string[]
): string[] {
  const d = def ?? [];
  if (d.includes(IMPRESION_POR_DEFECTO.NINGUNO)) return [];
  if (d.includes(IMPRESION_POR_DEFECTO.TODOS))
    return [...sectoresDisponibles];
  return d.filter(
    s =>
      s !== IMPRESION_POR_DEFECTO.NINGUNO &&
      s !== IMPRESION_POR_DEFECTO.TODOS &&
      s !== IMPRESION_POR_DEFECTO.DISPOSITIVO_ACTUAL &&
      sectoresDisponibles.includes(s)
  );
}

const sectoresImpresionLista = computed(() =>
  listaSectoresImpresionDesdeOg(userStore.opcionesGenerales)
);

const emit = defineEmits<{
  (e: "cerrar"): void;
}>();

onBeforeMount(async () => {
  if (productosStore.productos.length === 0)
    await productosStore.getFromFirestore();
  // En algunos entornos (ej. testing) el store persistido puede venir vacío.
  // Si no cargamos las tasas, el campo "Precio con IVA" puede no renderizarse.
  if (tasasStore.tasas.length === 0) await tasasStore.getTasasFromFirestore();
});

const formData = reactive({
  codigo: "",
  nombre: "",
  observaciones: "",
  categoriaId: "",
  tasaId: userStore.opcionesGenerales.tasaIVADefecto,
  precioSinIva: 0,
  precioConIva: 0,
  moneda: userStore.opcionesGenerales.monedaDefecto,
  impresionSectores: expandImpresionPorDefectoASectoresFormulario(
    userStore.opcionesGenerales.impresionPorDefecto,
    listaSectoresImpresionDesdeOg(userStore.opcionesGenerales)
  )
});


console.log(formData.impresionSectores);
// Mantener la selección de "Sectores impresión" alineada con Opciones Generales,
// pero sin pisar lo que el usuario marque manualmente.
const aplicandoDefaultImpresion = ref(false);
const usuarioTocóImpresión = ref(false);

watch(
  [
    () => userStore.opcionesGenerales.impresionPorDefecto,
    () => sectoresImpresionLista.value
  ],
  () => {
    if (usuarioTocóImpresión.value) return;
    aplicandoDefaultImpresion.value = true;
    formData.impresionSectores = expandImpresionPorDefectoASectoresFormulario(
      userStore.opcionesGenerales.impresionPorDefecto,
      sectoresImpresionLista.value
    );
    aplicandoDefaultImpresion.value = false;
  },
  { immediate: true, deep: true }
);

watch(
  () => formData.impresionSectores,
  val => {
    if (aplicandoDefaultImpresion.value) return;
    const esperado = expandImpresionPorDefectoASectoresFormulario(
      userStore.opcionesGenerales.impresionPorDefecto,
      sectoresImpresionLista.value
    );
    const a = [...(val ?? [])].slice().sort();
    const b = [...esperado].slice().sort();
    usuarioTocóImpresión.value = JSON.stringify(a) !== JSON.stringify(b);
  },
  { deep: true }
);

const rules = {
  codigo: {},
  nombre: {
    required: helpers.withMessage(
      "El campo nombre no puede estar vacío",
      required
    ),
    maxLength: helpers.withMessage(
      "El nombre no puede superar los 500 caracteres",
      maxLength(500)
    )
  },
  observaciones: {
    maxLength: helpers.withMessage(
      "Las observaciones no pueden superar los 500 caracteres",
      maxLength(500)
    )
  },
  categoriaId: {
    required: helpers.withMessage(
      "Debe seleccionar una categoria",
      (value: any) => {
        return value !== null && value !== undefined && value !== "";
      }
    )
  },
  moneda: {
    required: helpers.withMessage(
      "Debe seleccionar una moneda",
      (value: any) => {
        return value !== null && value !== undefined && value !== "";
      }
    )
  },
  tasaId: {
    required: helpers.withMessage(
      "Debe seleccionar una tasa de iva",
      (value: any) => {
        return value !== null && value !== undefined && value !== "";
      }
    )
  },
  precioSinIva: {},
  precioConIva: {}
};

const validate = useVuelidate(rules, toRefs(formData));

const handlerCrear = async () => {
  try {
    errorCreacion.value = "";
    const result = await validate.value.$validate();
    cargando.value = true;
    if (result) {
      // Asegurarnos de que precioConIva no quede en 0 por cambios no disparados.
      const tasa = tasasStore.getTasaIVAById(formData.tasaId);
      if (tasa && !isNaN(tasa.valor)) {
        const rate = 1 + tasa.valor / 100;
        const sin = formData.precioSinIva || 0;
        const con = formData.precioConIva || 0;

        if (sin > 0 && (con === 0 || isNaN(con))) {
          formData.precioConIva = round(sin * rate, 2);
        } else if (con > 0 && (sin === 0 || isNaN(sin))) {
          formData.precioSinIva = round(con / rate, 2);
        }
      }

      const categoriaNombre =
        categoriasStore.getCategoriaById(formData.categoriaId)?.nombre ?? null;

      let producto: Producto = {
        id: "",
        version: 1,
        nombre: formData.nombre.trim(),
        precioConIva: formData.precioConIva,
        precioSinIva: formData.precioSinIva,
        tasaIvaId: formData.tasaId,
        tasaIvaNombre: tasasStore.getTasaIVAById(formData.tasaId)?.nombre!,
        categoriaId: formData.categoriaId,
        categoriaNombre,
        codigo: formData.codigo,
        observaciones: formData.observaciones.trim() || null,
        impresionSectores: (() => {
          const sectoresDisponibles = listaSectoresImpresionDesdeOg(
            userStore.opcionesGenerales
          );
          const general = "General";

          // Nos aseguramos de no guardar tokens especiales (ej. "ninguno"/"todos")
          // que no existen como sectores de impresión reales.
          const validos = (formData.impresionSectores ?? []).filter(s =>
            sectoresDisponibles.includes(s)
          );

          if (validos.length > 0) return validos;
          return sectoresDisponibles.includes(general) ? [general] : [];
        })(),
        activo: true,
        fotoUrl: null,
        fotoStoragePath: null
      };

      const creadoId = await productosStore.crearProducto(producto);
      if (creadoId) {
        if (fotoFile.value) {
          await productosStore.subirFotoProducto(
            creadoId,
            formData.nombre,
            fotoFile.value
          );
        }
        mensajesStore.mostrarToast(
          "Producto creado",
          `Se creó el producto "${formData.nombre}" correctamente.`,
          mensajesStore.mensajeType.exito
        );
        emit("cerrar");
      } else {
        errorCreacion.value = "No se pudo crear el producto. Intentá de nuevo.";
      }
    } else {
      errorCreacion.value = "No se pudo crear el producto. Intentá de nuevo.";
    }
  } finally {
    cargando.value = false;
  }
};

const changePrecioTasa = (precioSinIva: number) => {
  const tasa = tasasStore.getTasaIVAById(formData.tasaId);

  if (tasa != null && !isNaN(precioSinIva) && !isNaN(tasa.valor)) {
    formData.precioConIva = round(precioSinIva * (1 + tasa.valor / 100), 2);
  }
};

const changePrecioConIva = (precioSinIva: number) => {
  formData.precioSinIva = round(precioSinIva, 2);
  const tasa = tasasStore.getTasaIVAById(formData.tasaId);
  if (tasa && !isNaN(precioSinIva) && !isNaN(tasa.valor)) {
    formData.precioConIva = round(precioSinIva * (1 + tasa.valor / 100), 2);
  }
};

const changePrecioSinIva = (precioConIva: number) => {
  formData.precioConIva = precioConIva;
  const tasa = tasasStore.getTasaIVAById(formData.tasaId);

  if (tasa != null && !isNaN(precioConIva) && !isNaN(tasa.valor)) {
    formData.precioSinIva = round(precioConIva / (1 + tasa.valor / 100), 2);
  }
};

const onFotoSelected = (event: Event) => {
  const input = event.target as HTMLInputElement;
  fotoFile.value = input?.files?.[0] ?? null;
};
</script>

<template>
  <Preview class="box mt-5">
    <form class="validate-form p-4" @submit.prevent="">
      <div
        class="border-b sm:flex-row border-slate-200/60 dark:border-darkmode-400"
      >
        <h2 class="mr-auto text-lg font-medium m-5">Agregar nuevo producto</h2>
        <div class="col-span-12 lg:col-span-6">
          <!-------------------- codigo ------------------------>
          <!-- <div class="flex mt-5">
            <div class="flex-none text-right pr-5 mt-2">
              <FormLabel
                htmlFor="horizontal-form-1"
                class="w-20 md:w-36 sw:w-20"
              >
                Código
              </FormLabel>
            </div>
            <div class="flex-1 w-1/3">
              <FormInput
                type="text"
                v-model.trim="validate.codigo.$model"
                :class="{
                  'border-danger': validate.codigo.$error
                }"
                placeholder="Código"
              />
              <template v-if="validate.codigo.$error">
                <div
                  v-for="(error, index) in validate.codigo.$errors"
                  :key="index"
                  class="mt-2 text-danger"
                >
                  {{ error.$message }}
                </div>
              </template>
            </div>
          </div> -->

          <!-------------------- Nombre ------------------------>
          <div class="flex mt-5">
            <div class="flex-none text-right pr-5 mt-2">
              <FormLabel
                htmlFor="horizontal-form-1"
                class="w-20 md:w-36 sw:w-20"
              >
                Nombre
              </FormLabel>
            </div>
            <div class="flex-1 w-1/3">
              <FormInput
                type="text"
                v-model.trim="validate.nombre.$model"
                :class="{
                  'border-danger': validate.nombre.$error
                }"
                placeholder="Nombre"
              />
              <template v-if="validate.nombre.$error">
                <div
                  v-for="(error, index) in validate.nombre.$errors"
                  :key="index"
                  class="mt-2 text-danger"
                >
                  {{ error.$message }}
                </div>
              </template>
            </div>
          </div>
          <!-------------------- Categoria ------------------------>
          <div class="flex mt-5">
            <div class="flex-none text-right pr-5 mt-2">
              <FormLabel
                htmlFor="horizontal-form-1"
                class="w-20 md:w-36 sw:w-20"
              >
                Categoría
              </FormLabel>
            </div>
            <div class="flex-1">
              <TomSelect
                v-model="formData.categoriaId"
                :class="{
                  'border-danger': validate.categoriaId.$error
                }"
                placeholder="Seleccione categoría"
              >
                <option
                  v-if="formData.categoriaId"
                  :value="formData.categoriaId"
                >
                  {{
                    categoriasStore.getCategoriaById(formData.categoriaId)
                      ?.nombre
                  }}
                </option>
                <option v-else :value="formData.categoriaId">
                  Seleccionar
                </option>
                <option
                  v-for="rubro in categoriasActivasOrdenadas"
                  :key="rubro.id"
                  :value="rubro.id"
                >
                  {{ rubro.nombre }}
                </option>
              </TomSelect>
            </div>
          </div>

          <!-------------------- Sectores de impresión ------------------------>
          <div class="flex mt-5" v-if="sectoresImpresionLista.length">
            <div class="flex-none text-right pr-5 mt-2">
              <FormLabel class="w-20 md:w-36 sw:w-20">
                Sectores impresión
              </FormLabel>
            </div>
            <div class="flex-1">
              <div class="flex flex-wrap gap-3">
                <FormCheck
                  v-for="sector in sectoresImpresionLista"
                  :key="sector"
                  class="flex items-center"
                >
                  <FormCheck.Input
                    type="checkbox"
                    :id="`sector-${sector}`"
                    :value="sector"
                    v-model="formData.impresionSectores"
                  />
                  <FormCheck.Label :for="`sector-${sector}`" class="ml-1">
                    {{ sector }}
                  </FormCheck.Label>
                </FormCheck>
              </div>
              <p class="mt-1 text-xs text-slate-500">
                Podés seleccionar ninguno, uno o varios sectores.
              </p>
            </div>
          </div>

          <!-------------------- Foto (opcional) ------------------------>
          <div class="flex mt-5">
            <div class="flex-none text-right pr-5 mt-2">
              <FormLabel htmlFor="producto-foto" class="w-20 md:w-36 sw:w-20">
                Imagen (opcional)
              </FormLabel>
            </div>
            <div class="flex-1">
              <input
                id="producto-foto"
                type="file"
                accept="image/*"
                class="hidden"
                @change="onFotoSelected"
              />
              <label
                for="producto-foto"
                class="mt-1 inline-flex items-center justify-center text-sm font-semibold text-slate-500 cursor-pointer select-none rounded-md border border-slate-200 bg-primary text-white py-2 px-4 text-center hover:bg-primary/90"
              >
                Adjuntar imagen
              </label>
            </div>
          </div>

          <div class="flex mt-5">
            <div class="flex-none text-right pr-5 mt-2">
              <FormLabel
                htmlFor="horizontal-form-1"
                class="w-20 md:w-36 sw:w-20"
              >
                Precio
                {{
                  monedasStore.getMonedaById(
                    userStore.opcionesGenerales.monedaDefecto
                  )?.simbolo
                }}
              </FormLabel>
            </div>
            <div class="flex-1 w-1/3">
              <InputNumero
                v-model:valor="formData.precioConIva"
                @change="
                  (valor: number) => {
                    changePrecioSinIva(valor);
                  }
                "
                :autoSeleccionar="true"
                :precision="2"
                :align="`left`"
                :class="{
                  'border-danger': validate.$error
                }"
                placeholder="Precio con iva"
              />
              <template v-if="validate.precioConIva.$error">
                <div
                  v-for="(error, index) in validate.precioConIva.$errors"
                  :key="index"
                  class="mt-2 text-danger"
                >
                  {{ error.$message }}
                </div>
              </template>
            </div>
          </div>

          <!-------------------- Precios ------------------------>
          <!-- <div class="mr-auto text-sm font-medium m-5 overflow-x-auto">
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th class="min-w-28">Precio con IVA</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td
                    v-if="tasasStore.getTasaIVAById(formData.tasaId) != null"
                  >
                    <InputNumero
                      v-model:valor="formData.precioConIva"
                      @change="
                        (valor: number) => {
                          changePrecioSinIva(valor);
                        }
                      "
                      :autoSeleccionar="true"
                      :precision="2"
                      :align="`left`"
                      :class="{
                        'border-danger': validate.$error
                      }"
                      placeholder="Precio con iva"
                    />
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </div> -->
           <!-------------------- Observaciones ------------------------>
           <div class="flex mt-5">
            <div class="flex-none text-right pr-5 mt-2">
              <FormLabel
                htmlFor="producto-observaciones"
                class="w-20 md:w-36 sw:w-20"
              >
                Notas
              </FormLabel>
            </div>
            <div class="flex-1 w-1/3">
              <FormTextarea
                id="producto-observaciones"
                v-model="validate.observaciones.$model"
                rows="3"
                :class="{
                  'border-danger': validate.observaciones.$error
                }"
                placeholder="Observaciones..."
              />
              <template v-if="validate.observaciones.$error">
                <div
                  v-for="(error, index) in validate.observaciones.$errors"
                  :key="index"
                  class="mt-2 text-danger"
                >
                  {{ error.$message }}
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-------------------- Validación final ------------------------>
        <Alert
          variant="outline-danger"
          class="flex items-center my-4"
          v-if="errorCreacion"
        >
          <Lucide icon="AlertCircle" class="w-6 h-6 mr-2" />
          <span>{{ errorCreacion }}</span>
        </Alert>

        <Alert
          variant="outline-danger"
          class="flex items-center my-4"
          v-if="validate?.$error"
        >
          <Lucide icon="AlertCircle" class="w-6 h-6 mr-2" />
          <ul>
            <li v-for="e in validate.$errors">{{ e.$message }}</li>
          </ul>
        </Alert>

        <div v-if="!cargando" class="flex mt-6">
          <!-------------------- Botón Cancelar ------------------------>
          <div class="flex-1 text-left">
            <Button
              variant="secondary"
              class="mb-2 mr-1"
              @click="() => emit('cerrar')"
            >
              Cancelar
            </Button>
          </div>
          <!-------------------- Botón Crear nuevo ------------------------>
          <div class="flex-1 text-right">
            <Button variant="primary" class="mb-2 mr-1" @click="handlerCrear">
              Crear nuevo
            </Button>
          </div>
        </div>
        <div v-else class="flex my-6">
          <div class="mx-auto">
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
      </div>
    </form>
  </Preview>
</template>
